import { useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { BUTTON_VARIANTS } from 'ui/button';
import { InputText } from 'ui/inputs';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';
import { Delete } from 'ui/icons';
import { BasicSelect } from 'ui/selects';
import {
  ButtonController,
  ButtonTypeVariationContainer,
  ButtonTypeVariationHeaderContainer,
  DeleteIconWrapper,
  InputWrapper,
} from 'modules/templateEditor/common/styles';
import { RadioButtonGroup } from '../RadioButtonGroup';
import {
  ACTION_BUTTON_DROP_DOWN_SELECTIONS,
  displayContent,
  ExpandableChevron,
} from '../../common';
import {
  PushActionButton,
  PushActionButtonType,
  PushContentDefinition,
} from '../../../graphql/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { EditableTitle } from '../EditableTitle';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

const StyledFormField = styled(FormField)`
  margin-top: 0 !important;
`;

const DescriptionText = styled.div`
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.spacing.utils.pxToRem(14)};
  line-height: ${({ theme }) => theme.spacing.utils.pxToRem(20)};
  padding-bottom: ${spacing.fiveEights};
`;

const StyledBasicSelect = styled(BasicSelect)`
  width: 100%;
`;

const ButtonActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PlaceholderInputWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  position: relative;
  width: 100%;
`;

const StyledInputText = styled(InputText)`
  padding-right: ${spacing.doubleHalf};
`;

type Props = {
  value: PushActionButton;
  index: number;
  draggableProvided: DraggableProvided;
};

const ButtonTypeOptions = [
  {
    id: PushActionButtonType.default,
    label: ACTION_BUTTON_DROP_DOWN_SELECTIONS.NORMAL,
    value: PushActionButtonType.default,
  },
  {
    id: PushActionButtonType.destructive,
    label: ACTION_BUTTON_DROP_DOWN_SELECTIONS.DESTRUCTIVE,
    value: PushActionButtonType.destructive,
  },
  {
    id: PushActionButtonType.textInput,
    label: ACTION_BUTTON_DROP_DOWN_SELECTIONS.TEXT_INPUT,
    value: PushActionButtonType.textInput,
  },
];

enum INPUT_FIELDS {
  INPUT_PLACEHOLDER = 'inputPlaceholder',
  INPUT_TITLE = 'inputTitle',
  IDENTIFIER = 'identifier',
}

export const ButtonTypeVariations = ({ value, index, draggableProvided }: Props) => {
  const { liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const pushMediumData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const actionButtonData = (pushMediumData?.actionButtons || []) as PushActionButton[];
  const actionButton = (actionButtonData[index] || {}) as PushActionButton;
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { ids, placeholders } = SETTINGS_DISPLAY_CONTENT.actionButtons;

  const onChangeUpdateInputText = (field: string, newText: string) => {
    const newActionButton = {
      ...value,
      [field]: newText,
    };
    actionButtonData[index] = newActionButton;
    setCurrentMediumField('actionButtons', actionButtonData);
  };

  /** On change button type, use initial button state
   * since we don't want the previous data to carry over to new button type.
   * */
  const onChangeButtonType = (buttonType: PushActionButtonType) => {
    const newActionButton = {
      ...actionButtonData[index], // preserve previous data,
      openApp: buttonType === PushActionButtonType.default,
      buttonType, // we are updating button type so use buttonType param.
    };
    actionButtonData[index] = newActionButton;
    setCurrentMediumField('actionButtons', actionButtonData);
  };

  return (
    <ButtonTypeVariationContainer data-test="action-button-tile">
      <ButtonTypeVariationHeaderContainer isExpanded={isExpanded}>
        <EditableTitle data={value} index={index} draggableProvided={draggableProvided} />
        <ButtonController data-test="action-button-header-ctrl">
          <DeleteIconWrapper
            onClick={() => {
              actionButtonData.splice(index, 1);
              setCurrentMediumField('actionButtons', actionButtonData);
            }}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Delete width={16} height={16} />
          </DeleteIconWrapper>
          <ExpandableChevron
            isExpanded={isExpanded}
            onClick={(result: boolean) => setIsExpanded(result)}
          />
        </ButtonController>
      </ButtonTypeVariationHeaderContainer>

      {isExpanded && (
        <InputWrapper gap>
          <div>
            <FormField isHorizontalLayout>
              <DescriptionText>{displayContent.labelButtonType}</DescriptionText>
              <StyledBasicSelect
                options={ButtonTypeOptions}
                defaultValues={[value.buttonType]}
                onSelect={(selectedType) => {
                  const buttonType = selectedType[0] as PushActionButtonType;
                  onChangeButtonType(buttonType);
                }}
              />
            </FormField>
          </div>

          <ButtonActionWrapper>
            {value.buttonType === PushActionButtonType.textInput && (
              <FormField labelText={displayContent.labelPlaceholder}>
                <PlaceholderInputWrapper>
                  <StyledInputText
                    id={ids.inputFieldPlaceholderId}
                    onChange={(newText) =>
                      onChangeUpdateInputText(INPUT_FIELDS.INPUT_PLACEHOLDER, newText)
                    }
                    placeholder={placeholders.inputFieldPlaceholder}
                    value={value.inputPlaceholder || ''}
                    width={'100%'}
                  />
                </PlaceholderInputWrapper>
              </FormField>
            )}
            {value.buttonType === PushActionButtonType.textInput && (
              <StyledFormField
                labelText={displayContent.labelSubmitButtonText}
                helpText={displayContent.strActionButtonHelpText}
              >
                <PlaceholderInputWrapper>
                  <StyledInputText
                    id={ids.inputFieldTitleId}
                    onChange={(newText) =>
                      onChangeUpdateInputText(INPUT_FIELDS.INPUT_TITLE, newText)
                    }
                    placeholder={placeholders.inputFieldTitlePlaceholder}
                    value={actionButton?.inputTitle || ''}
                    width={'100%'}
                  />
                </PlaceholderInputWrapper>
              </StyledFormField>
            )}
            <div>
              <DescriptionText>{displayContent.labelButtonAction}</DescriptionText>
              <RadioButtonGroup index={index} />
            </div>
          </ButtonActionWrapper>

          <div>
            <FormField
              labelText={displayContent.labelIdentifier}
              helpText={displayContent.strActionButtonHelpText}
            >
              <PlaceholderInputWrapper>
                <StyledInputText
                  id={ids.inputFieldIdentifierId}
                  onChange={(newText) => onChangeUpdateInputText(INPUT_FIELDS.IDENTIFIER, newText)}
                  placeholder={placeholders.inputFieldIdentifierPlaceholder}
                  value={value.identifier || ''}
                  width={'100%'}
                />
              </PlaceholderInputWrapper>
            </FormField>
          </div>
        </InputWrapper>
      )}
    </ButtonTypeVariationContainer>
  );
};
